import { useEffect, useState } from 'react';
import { Fab, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@malash-office/ui-components';
import { Ai } from '@/assets/icons/Ai';
import { useAgentTools } from '@/api/agent/tools';
import { MOBILE_BOTTOM_NAV_INSET } from '@/components/layout/mobileLayoutConstants';
import AgentChatPanel from './AgentChatPanel';

/**
 * The floating launcher, and the gate deciding whether the chat exists for this user at all.
 *
 * `agent:use` is a role-level switch, so the launcher must not render for a role that lacks it —
 * otherwise the first thing those users get is a 403. Asking is cheap: `/agent/tools` answers 403
 * for exactly that case, the query does not retry, and the answer is cached for the session.
 */
export const AgentChatLauncher = () => {
  const [open, setOpen] = useState(false);
  // Mounted from the first open onward, so the conversation survives close and reopen.
  const [everOpened, setEverOpened] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data, isError } = useAgentTools();

  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);

  if (isError || !data) {
    return null;
  }

  return (
    <>
      {everOpened && <AgentChatPanel open={open} onClose={() => setOpen(false)} />}
      <Fab
        size="medium"
        onClick={() => setOpen(previous => !previous)}
        aria-label={open ? 'סגירת עוזר AI' : 'פתיחת עוזר AI'}
        sx={{
          position: 'fixed',
          // The RTL stylis plugin mirrors `left`, so this renders on the visual right. The
          // desktop offset clears the sidebar; on mobile there is no persistent sidebar, so it
          // sits near the edge and clears the floating bottom nav instead.
          left: isSmallScreen ? 16 : 110,
          bottom: isSmallScreen ? MOBILE_BOTTOM_NAV_INSET + 12 : 24,
          // On mobile every drawer and dialog is full screen, so a closed launcher above them sat on
          // their fields and buttons. There it stays under drawers (`zIndex.drawer`, below modals)
          // until its own panel is open; its offset already keeps it clear of the bottom nav.
          zIndex: isSmallScreen && !open ? theme.zIndex.drawer - 1 : theme.zIndex.modal + 50,
          bgcolor: 'background.paper',
          color: 'primary.main',
          boxShadow: 4,
          '&:hover': { bgcolor: 'grey.100' }
        }}
      >
        {open ? <Icon icon="close" color="inherit" /> : <Ai />}
      </Fab>
    </>
  );
};

export default AgentChatLauncher;
